import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";

const responseSchema = z.object({
  message: z.string(),
});

type Response = z.infer<typeof responseSchema>;

export const deleteSession = async ({
  lang,
  token,
  sessionId,
}: {
  lang: Lang;
  token: string;
  sessionId: string;
}): Promise<Response> => {
  const endpoint = `/winwin-gpt/session/${sessionId}`;
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "DELETE",
    headers: { "Accept-Language": lang, "X-User-Profile": token },
    cache: "no-cache",
  });

  if (!res.ok) throw new Error(`API failed (${endpoint})`);
  return responseSchema.parse(await res.json());
};

export const useDeleteSession = () => {
  const queryClient = useQueryClient();
  const { lang } = useLang();
  const { backendToken } = useAuth();
  return useMutation(
    ({ sessionId }: { sessionId: string }) =>
      deleteSession({ lang, token: backendToken ?? "", sessionId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["session-list", lang]);
      },
    }
  );
};
